import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '../layout/Column';
import { SelectField } from '../form/SelectField';
import { FieldsGroup } from '../form/FieldsGroup';
import Checkbox from '../form/Checkbox';
import { Row } from '../layout/Row';
import { WebPlayerHeaderProps } from '../../types/webPlayer';
import { useLazyQueryCached } from '../utils/graphql';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import { GroundUpdateTypeRes, GroundUpdateTypeVars, NEAREST_GROUND } from '../../query/grounds';
import { GroundType } from '../../models/ground';

const findGroundOptionById = (groundSet: GroundType[], id: string) => groundSet.find((g) => g.id === id) || null;

const WebPlayerHeader = (props: WebPlayerHeaderProps) => {
  const { t } = useTranslation();
  const {
    ground,
    groundOptions,
    groundSet,
    isSessionDataReady,
    jerseyOrNumber,
    sessionId,
    setGround,
    setJerseyOrNumber,
    setTrailsEnabled,
    trailsEnabled,
  } = props;

  const [nearestGround, setNearestGround] = React.useState<GroundType | false | null>(false);

  const handleGroundSelectChange = useCallback((e) => {
    const selectedName = e.target.value;
    const selectedGround = groundSet.filter((g) => g.name === selectedName)[0];

    setGround(selectedGround);
  }, [groundSet, setGround]);

  const handleJerseySelectChange = useCallback(() => {
    setJerseyOrNumber((prev) => !prev);
  }, [setJerseyOrNumber]);

  const handleTrailsSelectChange = useCallback(
    () => setTrailsEnabled((prev) => !prev),
    [setTrailsEnabled],
  );

  const [getNearestGround] = useLazyQueryCached<GroundUpdateTypeRes, GroundUpdateTypeVars>(NEAREST_GROUND, {
    variables: {
      teamsessionId: sessionId,
    },
    ...CACHE_AND_NETWORK,
    onCompleted: (data) => {
      if (data.res.bestGround) {
        setNearestGround(findGroundOptionById(groundSet, data.res.bestGround.id));
      } else if (data.res.candidateGroundsList.length > 0) {
        setNearestGround(findGroundOptionById(groundSet, data.res.candidateGroundsList[0].id));
      } else {
        setNearestGround(null);
      }
    },
  });

  useEffect(() => {
    if (nearestGround === false && groundSet && groundSet.length > 0) {
      getNearestGround();
    }
  }, [groundSet, nearestGround]);

  useEffect(() => {
    if (nearestGround) {
      setGround(nearestGround);
    }
  }, [nearestGround]);

  return (
    <Row style={{ paddingLeft: '10px', paddingRight: '30px' }}>
      <Column xs={8}>
        <Row>

          <Column xs={4}>
            <SelectField
              required
              fullWidth
              label={t('grounds.ground', 'ground')}
              value={ground?.name || ''}
              options={groundOptions}
              onChange={handleGroundSelectChange}
              disabled={!isSessionDataReady}
            />
          </Column>
          <Column xs={6}>
            <FieldsGroup
              label={`${
                t('athlete.widgets.label.shortName', 'short name')
              }/${
                t('athlete.widgets.label.jersey', 'jersey')
              }`}
              onChange={handleJerseySelectChange}
              options={[
                {
                  value: true,
                  label: t('athlete.widgets.label.shortName', 'short name'),
                },
                {
                  value: false,
                  label: t('athlete.widgets.label.jersey', 'jersey'),
                },
              ]}
              type="radio"
              value={jerseyOrNumber}
            />
          </Column>
          <Column xs={2}>
            <Checkbox
              value={trailsEnabled}
              label={t('session.labels.trails', 'trails')}
              onClick={handleTrailsSelectChange}
            />

          </Column>

        </Row>
      </Column>
    </Row>
  );
};

export default memo(WebPlayerHeader);
